import { useEffect, useRef } from 'react';

type BackHandler = () => boolean | void;

interface StackItem {
  id: string;
  handler: BackHandler;
  priority: number;
  createdAt: number;
}

// Global stack of active back handlers (modals, sheets, popups)
const backHandlers: StackItem[] = [];

/**
 * Register a back button handler. Returns an unregister function.
 */
export function registerBackHandler(id: string, handler: BackHandler, priority = 0): () => void {
  // Remove existing with same id if any
  const existingIdx = backHandlers.findIndex((item) => item.id === id);
  if (existingIdx !== -1) {
    backHandlers.splice(existingIdx, 1);
  }

  backHandlers.push({
    id,
    handler,
    priority,
    createdAt: Date.now(),
  });

  // Sort by priority (higher first), then by newest first
  backHandlers.sort((a, b) => b.priority - a.priority || b.createdAt - a.createdAt);

  return () => {
    const idx = backHandlers.findIndex((item) => item.id === id);
    if (idx !== -1) {
      backHandlers.splice(idx, 1);
    }
  };
}

/**
 * Trigger the topmost registered back handler.
 * Returns true if a handler was executed, false otherwise.
 */
export function triggerBackHandler(): boolean {
  if (backHandlers.length === 0) return false;

  const top = backHandlers.shift();
  if (top) {
    try {
      const result = top.handler();
      // If handler explicitly returned false, it didn't consume the event
      if (result === false) return false;
      return true;
    } catch (e) {
      console.error('[useBackButton] Error executing back handler:', e);
      return true;
    }
  }

  return false;
}

/**
 * Hook to automatically register an onClose callback for Android hardware back button and Escape key.
 *
 * @param isOpen Whether the modal/sheet is currently open
 * @param onClose Callback to close the modal/sheet
 * @param priority Optional priority (higher priority handlers are executed first)
 */
export function useBackButton(isOpen: boolean, onClose: () => void, priority = 0) {
  const idRef = useRef<string>(`back-handler-${Math.random().toString(36).substr(2, 9)}`);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;

    const unregister = registerBackHandler(
      idRef.current,
      () => {
        onCloseRef.current();
        return true;
      },
      priority
    );

    // Also support keyboard Escape for desktop / web
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      unregister();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, priority]);
}
